import React from 'react';

/**
 * Reusable project data loading overlay and error state component.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} [props.loading=false] - Whether project data is currently loading.
 * @param {boolean} [props.isFadingOut=false] - Whether fade-out animation is currently active.
 * @param {string|null} [props.error=null] - Error message if project data failed to load.
 * @param {Function} [props.onRetry] - Callback invoked when clicking the retry button on error.
 * @param {string} [props.title='Loading Project Data'] - Primary loading heading.
 * @param {string} [props.message='Please wait while we fetch your project information...'] - Detailed loading message.
 * @param {string} [props.subtext='This may take a few seconds'] - Supplementary loading subtext.
 * @param {string} [props.errorHeading='Error Loading Project'] - Heading displayed on error.
 * @param {string} [props.containerClass='viewProject viewProject-ch'] - Wrapper container class name.
 * @returns {JSX.Element|null} Rendered loading overlay, error alert, or null if idle.
 */
export default function ProjectDataLoader({
    loading = false,
    isFadingOut = false,
    error = null,
    onRetry = () => window.location.reload(),
    title = 'Loading Project Data',
    message = 'Please wait while we fetch your project information...',
    subtext = 'This may take a few seconds',
    errorHeading = 'Error Loading Project',
    containerClass = 'viewProject viewProject-ch',
}) {
    if (loading || isFadingOut) {
        return (
            <div className={`${containerClass} loading-container ${isFadingOut ? 'fade-out' : ''}`}>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center loading-content">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading project data...</span>
                        </div>
                        <h5 className="text-primary fw-bold mb-2">{title}</h5>
                        <p className="text-dark" style={{ fontSize: '1rem', fontWeight: '500' }}>
                            {message}
                        </p>
                        {subtext && (
                            <div className="mt-3">
                                <small className="text-muted">{subtext}</small>
                            </div>
                        )}
                    </div>
                </div>
                <style jsx>{`
                    .loading-container {
                        animation: fadeIn 0.3s ease-in;
                    }
                    .loading-content {
                        animation: slideUp 0.4s ease-out;
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from { 
                            opacity: 0; 
                            transform: translateY(20px);
                        }
                        to { 
                            opacity: 1; 
                            transform: translateY(0);
                        }
                    }
                    .loading-container.fade-out {
                        animation: fadeOut 0.3s ease-out forwards;
                    }
                    @keyframes fadeOut {
                        from { opacity: 1; }
                        to { opacity: 0; }
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div className={containerClass}>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="alert alert-danger" role="alert">
                            <h4 className="alert-heading">{errorHeading}</h4>
                            <p>{error}</p>
                            <hr />
                            <button 
                                className="btn btn-outline-primary"
                                onClick={onRetry}
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
